import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import TimePicker from '../../components/ui/TimePicker';
import DatePicker from '../../components/ui/DatePicker';
import { tempoTravellerRoutes } from '../../data/tempoTravellerRoutes';

const TempoTravellerBooking = () => {
  const { route: routeParam } = useParams();
  const navigate = useNavigate();
  
  const [routeData, setRouteData] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    date: '',
    time: '',
    pickupAddress: '',
    email: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [notificationStatus, setNotificationStatus] = useState({
    success: false,
    message: ''
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    
    if (routeParam && tempoTravellerRoutes?.[routeParam]) {
      setRouteData(tempoTravellerRoutes?.[routeParam]);
    } else {
      navigate('/');
    }
  }, [routeParam, navigate]);

  const handleVehicleSelect = (vehicle) => {
    setSelectedVehicle(vehicle);
    // Scroll to booking form
    setTimeout(() => {
      document.getElementById('booking-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleTimeSelect = (time) => {
    setFormData(prev => ({ ...prev, time }));
    if (errors?.time) {
      setErrors(prev => ({ ...prev, time: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.name?.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData?.phone?.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/?.test(formData?.phone)) {
      newErrors.phone = 'Phone number must be 10 digits';
    }
    
    if (!formData?.date) {
      newErrors.date = 'Date is required';
    }
    
    if (!formData?.time) {
      newErrors.time = 'Time is required';
    }
    
    if (!formData?.pickupAddress?.trim()) {
      newErrors.pickupAddress = 'Pickup address is required';
    }
    
    if (formData?.email && !/\S+@\S+\.\S+/?.test(formData?.email)) {
      newErrors.email = 'Invalid email format';
    }
    
    return newErrors;
  };

  const sendNotificationEmail = async () => {
    try {
      const serviceId = import.meta.env?.VITE_EMAILJS_SERVICE_ID;
      const templateId = import.meta.env?.VITE_EMAILJS_TEMPLATE_ID;
      const publicKey = import.meta.env?.VITE_EMAILJS_PUBLIC_KEY;

      if (!serviceId || !templateId || !publicKey || 
          serviceId?.includes('your-') || templateId?.includes('your-') || publicKey?.includes('your-')) {
        console.warn('EmailJS not configured. Skipping email notification.');
        setNotificationStatus({
          success: false,
          message: 'Email service not configured. Please contact support directly.'
        });
        return false;
      }

      const templateParams = {
        to_name: 'Advait Tour and Travels',
        to_email: 'advaittourtravel666@gmail.com',
        business_phone: '7998666660',
        customer_name: formData?.name,
        customer_phone: formData?.phone,
        customer_email: formData?.email || 'Not provided',
        pickup_date: formData?.date,
        pickup_time: formData?.time,
        pickup_address: formData?.pickupAddress,
        route_from: routeData?.from || '',
        route_to: routeData?.to || '',
        vehicle_name: selectedVehicle?.name,
        vehicle_seats: selectedVehicle?.seats,
        vehicle_luggage: selectedVehicle?.luggage,
        vehicle_price: selectedVehicle?.price?.toLocaleString(),
        tour_duration: '1 Days',
        special_message: formData?.message || 'No special requirements',
        booking_reference: Math.random()?.toString(36)?.substr(2, 9)?.toUpperCase()
      };

      await emailjs?.send(
        serviceId,
        templateId,
        templateParams,
        publicKey
      );

      setNotificationStatus({
        success: true,
        message: 'Notification sent successfully to business owner'
      });
      return true;
    } catch (error) {
      console.error('Failed to send notification:', error);
      setNotificationStatus({
        success: false,
        message: 'Failed to send notification. Please contact support directly.'
      });
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const newErrors = validateForm();
    
    if (Object.keys(newErrors)?.length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    
    await sendNotificationEmail();
    
    setTimeout(() => {
      setIsSubmitting(false);
      setShowConfirmationPopup(true);
    }, 1000);
  };

  const handleClosePopup = () => {
    setShowConfirmationPopup(false);
    navigate('/');
  };

  const handleCall = () => {
    window.location.href = 'tel:+917998666660';
  };

  if (!routeData) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex flex-col">
      <Header />
      <main className="flex-1 py-8 md:py-12 lg:py-16">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="bg-white rounded-2xl shadow-xl border-2 border-blue-100 p-6 md:p-8 mb-8">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-green-600 text-center mb-4">
              {routeData?.from} to {routeData?.to}
            </h1>
            <p className="text-center text-gray-600 text-lg">
              Select your preferred Tempo Traveller or Force Urbania
            </p>
          </div>

          <div className="mb-8">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-gray-800 mb-6">
              Available Vehicles
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {routeData?.vehicles?.map((vehicle) => (
                <div
                  key={vehicle?.id}
                  onClick={() => handleVehicleSelect(vehicle)}
                  className={`bg-white rounded-xl shadow-md hover:shadow-2xl transition-all duration-300 cursor-pointer border-2 transform hover:-translate-y-1 ${
                    selectedVehicle?.id === vehicle?.id
                      ? 'border-green-500 scale-105 shadow-2xl' : 'border-gray-200 hover:border-green-300'
                  }`}
                >
                  <div className="relative h-48 overflow-hidden rounded-t-xl">
                    <img
                      src={vehicle?.image}
                      alt={vehicle?.imageAlt || `${vehicle?.name} - ${vehicle?.seats} seater`}
                      className="w-full h-full object-cover"
                    />
                    {selectedVehicle?.id === vehicle?.id && (
                      <div className="absolute top-2 right-2 bg-green-500 text-white rounded-full p-2 shadow-lg">
                        <Icon name="Check" size={20} />
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="text-lg font-bold text-gray-800 mb-2">
                      {vehicle?.name}
                    </h3>
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Icon name="Users" size={16} />
                        <span>{vehicle?.seats} Seats</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Icon name="Briefcase" size={16} />
                        <span>{vehicle?.luggage} Luggage</span>
                      </div>
                    </div>
                    <div className="text-center pt-4 border-t border-gray-200">
                      <p className="text-2xl font-bold text-primary mb-2">
                        ₹{vehicle?.price?.toLocaleString()}/-
                      </p>
                      <p className="text-xs text-gray-500">Inc. of Taxes*</p>
                    </div>
                    <Button
                      variant={selectedVehicle?.id === vehicle?.id ? 'default' : 'outline'}
                      size="sm"
                      fullWidth
                      className="mt-4"
                    >
                      {selectedVehicle?.id === vehicle?.id ? 'Selected' : 'Select Car'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {selectedVehicle && (
            <div id="booking-form" className="bg-white rounded-2xl shadow-lg border border-border p-6 md:p-8 lg:p-10 mb-8">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-primary text-center mb-8">
                Booking Details
              </h2>
              
              {/* Booking Summary */}
              <div className="space-y-4 mb-8 pb-8 border-b border-border">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 font-medium">From:</span>
                  <span className="text-gray-800 font-semibold text-lg">{routeData?.from}</span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 font-medium">To:</span>
                  <span className="text-gray-800 font-semibold text-lg">{routeData?.to}</span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 font-medium">Vehicle:</span>
                  <span className="text-gray-800 font-semibold text-lg">
                    {selectedVehicle?.name} - {selectedVehicle?.luggage} 🧳 {selectedVehicle?.seats} 👥
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 font-medium">Price:</span>
                  <span className="text-primary font-bold text-xl">₹{selectedVehicle?.price?.toLocaleString()} Inc. of Taxes*</span>
                </div>
              </div>
              
              {/* Contact & Pickup Details Form */}
              <div>
                <h3 className="text-xl md:text-2xl font-heading font-bold text-foreground mb-6">
                  Contact & Pickup Details
                </h3>
                
                <form onSubmit={handleSubmit} className="space-y-5">
                  <Input
                    label="Name"
                    type="text"
                    name="name"
                    placeholder="Enter your full name"
                    value={formData?.name}
                    onChange={handleChange}
                    error={errors?.name}
                    required
                  />
                  
                  <Input
                    label="Phone"
                    type="tel"
                    name="phone"
                    placeholder="10-digit mobile number"
                    value={formData?.phone}
                    onChange={handleChange}
                    error={errors?.phone}
                    required
                  />
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input
                      label="Date"
                      name="date"
                      placeholder="Select date"
                      value={formData?.date}
                      onChange={handleChange}
                      error={errors?.date}
                      readOnly
                      onClick={() => setShowDatePicker(true)}
                      className="cursor-pointer"
                    />

                    <DatePicker
                      isOpen={showDatePicker}
                      onClose={() => setShowDatePicker(false)}
                      onSelect={(date) => {
                        handleChange({ target: { name: 'date', value: date } });
                        setShowDatePicker(false);
                      }}
                      initialDate={formData?.date}
                      minDate={new Date()?.toISOString()?.split('T')?.[0]}
                    />
                    
                    <div>
                      <Input
                        label="Time"
                        type="text"
                        name="time"
                        placeholder="Select time"
                        value={formData?.time}
                        onClick={() => setShowTimePicker(true)}
                        onChange={() => {}}
                        error={errors?.time}
                        required
                        readOnly
                        className="cursor-pointer"
                      />
                    </div>
                  </div>
                  
                  <Input
                    label="Pick up Address"
                    type="text"
                    name="pickupAddress"
                    placeholder="Enter pickup location"
                    value={formData?.pickupAddress}
                    onChange={handleChange}
                    error={errors?.pickupAddress}
                    required
                  />
                  
                  <Input
                    label="Email (Optional)"
                    type="email"
                    name="email"
                    placeholder="your.email@example.com"
                    value={formData?.email}
                    onChange={handleChange}
                    error={errors?.email}
                  />
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-foreground">
                      Message
                    </label>
                    <textarea
                      name="message"
                      rows="4"
                      placeholder="Any special requirements or instructions..."
                      value={formData?.message}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-md border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all duration-200 resize-none"
                    />
                  </div>
                  
                  <div className="pt-4">
                    <Button
                      type="submit"
                      variant="default"
                      size="lg"
                      loading={isSubmitting}
                      iconName="Calendar"
                      iconPosition="left"
                      fullWidth
                      className="bg-action hover:bg-action/90"
                    >
                      {isSubmitting ? 'Processing...' : 'Confirm Booking'}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          )}
          
          {/* 24/7 Support Section */}
          <div className="bg-gradient-to-r from-primary to-secondary rounded-2xl shadow-lg p-6 md:p-8 text-center text-white">
            <div className="flex items-center justify-center gap-2 mb-3">
              <Icon name="Clock" size={24} className="text-accent" />
              <h3 className="text-xl md:text-2xl font-heading font-bold">
                We're here for you 24/7!
              </h3>
            </div>
            <p className="text-white/90 mb-4 text-sm md:text-base">
              Give us a call anytime
            </p>
            <Button
              variant="outline"
              size="lg"
              onClick={handleCall}
              iconName="Phone"
              iconPosition="left"
              className="bg-white text-primary hover:bg-white/90 border-white font-bold text-lg"
            >
              +91-7998666660
            </Button>
          </div>
        </div>
      </main>
      <Footer />
      
      {/* Booking Confirmation Popup */}
      {showConfirmationPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 md:p-8 animate-in fade-in zoom-in duration-300">
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                <Icon name="CheckCircle" size={48} className="text-green-600" />
              </div>
            </div>
            
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-center text-foreground mb-4">
              Booking Confirmed!
            </h2>
            
            <p className="text-center text-muted-foreground mb-6 text-base">
              Your tempo traveller booking has been successfully confirmed.
            </p>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <Icon name="Bell" size={20} className="text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="w-full">
                  <h3 className="font-semibold text-foreground mb-2 text-sm">
                    Notifications Sent
                  </h3>
                  
                  {notificationStatus?.success ? (
                    <div className="space-y-3">
                      <p className="text-sm text-green-700 leading-relaxed mb-3 font-medium">
                        ✓ Booking confirmation sent successfully!
                      </p>
                      
                      <div className="mb-3 pb-3 border-b border-blue-200">
                        <p className="text-xs font-semibold text-blue-700 mb-2">Business Owner:</p>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <Icon name="Mail" size={16} className="text-green-600" />
                            <span className="text-sm text-foreground">advaittourtravel666@gmail.com ✓</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Icon name="Phone" size={16} className="text-green-600" />
                            <span className="text-sm text-foreground">+91-7998666660</span>
                          </div>
                        </div>
                      </div>
                      
                      <p className="text-xs text-muted-foreground italic">
                        Complete ride details including customer info, route, vehicle, date/time, and pickup address have been sent.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-sm text-amber-700 leading-relaxed mb-3">
                        {notificationStatus?.message || 'Booking recorded. Please contact support to confirm.'}
                      </p>
                      
                      <div className="mb-3 pb-3 border-b border-blue-200">
                        <p className="text-xs font-semibold text-blue-700 mb-2">Contact Business Owner:</p>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <Icon name="Mail" size={16} className="text-blue-600" />
                            <span className="text-sm text-foreground">advaittourtravel666@gmail.com</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Icon name="Phone" size={16} className="text-blue-600" />
                            <span className="text-sm text-foreground">+91-7998666660</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Booking Reference:</span>
                <span className="text-sm font-semibold text-foreground">#{Math.random()?.toString(36)?.substr(2, 9)?.toUpperCase()}</span>
              </div>
            </div>
            
            <div className="space-y-3">
              <Button
                variant="default"
                size="lg"
                onClick={handleClosePopup}
                fullWidth
                className="bg-action hover:bg-action/90"
              >
                Back to Home
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={handleCall}
                iconName="Phone"
                iconPosition="left"
                fullWidth
              >
                Contact Support
              </Button>
            </div>
          </div>
        </div>
      )}
      
      <TimePicker
        isOpen={showTimePicker}
        onClose={() => setShowTimePicker(false)}
        onSelect={handleTimeSelect}
        initialTime={formData?.time}
      />
    </div>
  );
};

export default TempoTravellerBooking;